import { Controller } from "@hotwired/stimulus"

// Cmd-K / Ctrl-K command palette. The catalog of jumps comes from the server
// (authorized the same way the sidebar is). Thread actions click a button that
// is already on the page — the same contract shortcuts_controller uses — so
// archive, snooze, assign and the rest stay one write path.
//
// Typing searches everything the viewer can see (FRO-148): an exact ticket id is the top row and
// Enter opens it; otherwise Tickets, Contacts, Articles and Commands, a few of each, then "See all
// results" for the full /search page. The server decides what is visible; this only arranges it.
//
// Digit shortcuts are deliberately absent: ticket ids are full of digits, and
// stealing "4" from "ABCD-4821" would make the navigate-by-id path fight itself.
const STORAGE_KEY = "frontier.commandPalette.recent"
const RECENT_LIMIT = 8
const SEARCH_DELAY = 250
const COMMAND_LIMIT = 5
const EMPTY_RESULTS = { commands: [], jump: null, tickets: [], contacts: [], articles: [] }
const PAGE_ACTIONS = [
  { id: "action:archive", label: "Archive", keywords: [ "done", "close" ], selector: '.thread-info [data-conversation-action="archive"]' },
  { id: "action:snooze", label: "Snooze", keywords: [ "later", "remind" ], selector: '.thread-info [data-conversation-action="snooze"]' },
  { id: "action:assign", label: "Assign", keywords: [ "owner", "assignee" ], selector: '.thread-info [data-conversation-action="assign"]' },
  { id: "action:request-type", label: "Set type", keywords: [ "request", "triage" ], selector: '.thread-info [data-conversation-action="request-type"]' },
  { id: "action:project-part", label: "Set part", keywords: [ "area", "triage" ], selector: '.thread-info [data-conversation-action="project-part"]' },
  { id: "action:audience", label: "Set audience", keywords: [ "sender", "segment", "member" ], selector: '.thread-info [data-conversation-action="audience"]' },
  { id: "action:priority", label: "Set priority", keywords: [ "urgent", "triage" ], selector: '.thread-info [data-conversation-action="priority"]' },
  { id: "action:duplicate", label: "Mark as duplicate", keywords: [ "copy" ], selector: '[data-conversation-action="duplicate"]' },
  // Reads the rail trigger's aria-label, "Move to project…", which keeps it apart from the archive
  // button's "Move to inbox". The keywords find it by the words people use for a box (FRO-150).
  // reveal: its picker is a fixed menu opened from the rail, so the rail has to be open and the
  // trigger on screen first — see revealTrigger.
  { id: "action:move", label: "Move to project", keywords: [ "inbox", "mailbox", "box" ], selector: '.thread-info [data-conversation-action="move"]', reveal: true },
  { id: "action:create-linear-issue", label: "Create Linear issue", keywords: [ "ticket", "file" ], selector: '[data-conversation-action="create-linear-issue"]' },
  { id: "action:insert-macro", label: "Insert macro", keywords: [ "template", "reply" ], selector: '#reply_composer [data-composer-target="templatesButton"]:not([hidden])' },
  { id: "action:insert-kb", label: "Insert from knowledge", keywords: [ "article", "kb" ], selector: '#reply_composer [data-composer-target="knowledgeButton"]:not([hidden])' }
]

export default class extends Controller {
  static targets = [ "query", "results", "empty", "groupTemplate", "optionTemplate" ]
  static values = { url: String, searchUrl: String }

  connect() {
    this.onKeydown = this.onKeydown.bind(this)
    this.apply(EMPTY_RESULTS)
  }

  disconnect() {
    this.removeKeyListener()
    clearTimeout(this.searchTimeout)
    this.abortSearch?.abort()
  }

  hotkey(event) {
    if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "k") return

    event.preventDefault()
    this.element.hidden ? this.open() : this.close()
  }

  async open() {
    this.previousFocus = document.activeElement
    this.element.hidden = false
    this.queryTarget.value = ""
    this.apply({ ...EMPTY_RESULTS, commands: this.commands })
    this.highlightMoved = false
    this.queryTarget.focus()
    document.addEventListener("keydown", this.onKeydown)
    this.apply(await this.fetchCatalog(""))
    this.render()
  }

  close() {
    if (this.element.hidden) return

    this.element.hidden = true
    this.removeKeyListener()
    clearTimeout(this.searchTimeout)
    this.pendingSearch = undefined
    this.abortSearch?.abort()
    if (this.previousFocus?.isConnected) this.previousFocus.focus()
  }

  filter() {
    const query = this.typedQuery

    this.highlightMoved = false
    this.render()
    clearTimeout(this.searchTimeout)
    this.pendingSearch = query
    this.searchTimeout = setTimeout(() => this.search(query), SEARCH_DELAY)
  }

  onKeydown(event) {
    if (this.element.hidden) return

    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      return
    }

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.moveHighlight(1)
      return
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      this.moveHighlight(-1)
      return
    }

    if (event.key === "Enter") {
      event.preventDefault()
      this.activateHighlighted({ newTab: event.metaKey || event.ctrlKey })
    }
  }

  // Enter typed faster than the debounce must not open whatever the PREVIOUS query left on top —
  // "hmsl10670⏎" means that ticket. So a search still waiting is run now and awaited first.
  async activateHighlighted({ newTab }) {
    if (this.pendingSearch !== undefined) {
      clearTimeout(this.searchTimeout)
      await this.search(this.pendingSearch)
    }

    const option = this.highlighted
    if (option) this.activate(option, { newTab })
  }

  run(event) {
    this.activate(event.currentTarget, { newTab: event.metaKey || event.ctrlKey })
  }

  activate(option, { newTab }) {
    const href = option.dataset.href
    const selector = option.dataset.selector

    this.remember(option.dataset.commandId)

    if (newTab && href) {
      window.open(href, "_blank", "noopener")
      return
    }

    this.close()

    // After this click has finished bubbling, not inside it. A picker trigger (Assign, Set type,
    // Set part, Set audience) opens its menu and starts listening for outside clicks on document —
    // and the option click still on its way up would arrive there as one, closing the menu it opened.
    if (selector) {
      const reveal = option.dataset.reveal === "true"
      setTimeout(() => {
        const button = document.querySelector(selector)
        if (!button) return
        if (!reveal) return button.click()

        this.revealTrigger(button)
        // A frame later, once the scroll it caused has been dispatched: a fixed menu closes on scroll.
        requestAnimationFrame(() => button.click())
      })
      return
    }

    if (href) window.Turbo.visit(href)
  }

  // A trigger in the rail has no box while the rail is collapsed, and a menu opened from it is an
  // invisible 0×0 panel that still holds the keys; scrolled out of view, a fixed menu opens off
  // screen beside it. So open the rail (its own toggle, as a click would) and bring the trigger into
  // view first.
  revealTrigger(button) {
    document.querySelector('.thread-info__collapse-toggle[aria-pressed="true"]')?.click()
    button.scrollIntoView({ block: "center", behavior: "instant" })
  }

  apply(payload) {
    this.commands = payload.commands || []
    this.jump = payload.jump || null
    this.tickets = payload.tickets || []
    this.contacts = payload.contacts || []
    this.articles = payload.articles || []
  }

  async search(query) {
    if (query !== this.typedQuery) return

    const payload = await this.fetchCatalog(query)
    if (query !== this.typedQuery) return

    if (this.pendingSearch === query) this.pendingSearch = undefined
    this.apply(payload)
    this.render()
  }

  async fetchCatalog(query) {
    this.abortSearch?.abort()
    this.abortSearch = new AbortController()

    const url = new URL(this.urlValue, window.location.origin)
    if (query) url.searchParams.set("q", query)
    const projectId = this.currentProjectId
    if (projectId) url.searchParams.set("project_id", projectId)

    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: this.abortSearch.signal
      })
      if (!response.ok) return EMPTY_RESULTS

      return await response.json()
    } catch (error) {
      if (error.name === "AbortError") return this.currentResults

      return EMPTY_RESULTS
    }
  }

  get currentResults() {
    return { commands: this.commands, jump: this.jump, tickets: this.tickets, contacts: this.contacts, articles: this.articles }
  }

  // A fresh query puts the highlight on the top row — the exact ticket when there is one. A row the
  // person moved to with the arrows or the pointer stays highlighted when results arrive under it.
  render() {
    const keep = this.highlightMoved ? this.highlighted?.dataset.commandId : null
    this.resultsTarget.replaceChildren()

    const query = this.typedQuery
    const groups = this.groupedItems(query)

    groups.forEach((group) => this.resultsTarget.appendChild(this.buildGroup(group)))
    this.emptyTarget.hidden = groups.length > 0
    const kept = keep && this.options.find((option) => option.dataset.commandId === keep)
    this.highlight(kept || this.options[0])
  }

  groupedItems(query) {
    const pageActions = this.pageActions().filter((item) => this.matches(item, query))
    const jumps = this.commands.filter((item) => this.matches(item, query))

    if (query === "") {
      const recents = this.recentItems([ ...pageActions, ...jumps ])
      const recentIds = new Set(recents.map((item) => item.id))
      return [
        { label: "Recent", items: recents },
        { label: "Thread", items: pageActions.filter((item) => !recentIds.has(item.id)) },
        { label: "Jump", items: jumps.filter((item) => !recentIds.has(item.id)) }
      ].filter((group) => group.items.length > 0)
    }

    const commands = this.rankCommands([ ...pageActions, ...jumps ], query).slice(0, COMMAND_LIMIT)
    return [
      { label: "Go to ticket", items: this.jump ? [ this.ticketItem(this.jump, { jump: true }) ] : [] },
      { label: "Tickets", items: this.tickets.map((ticket) => this.ticketItem(ticket)) },
      { label: "Contacts", items: this.contacts.map((contact) => this.contactItem(contact)) },
      { label: "Articles", items: this.articles.map((article) => this.articleItem(article)) },
      { label: "Commands", items: commands },
      { label: "", items: [ this.seeAllItem() ] }
    ].filter((group) => group.items.length > 0)
  }

  // A label that contains the query beats one that only spells it out letter by letter, so
  // "arch" puts Archive ahead of a label that merely spells it out when only five commands are shown.
  rankCommands(items, query) {
    const direct = (item) => (item.label.toLowerCase().includes(query) ? 0 : 1)
    return items.map((item, index) => ({ item, index }))
      .sort((a, b) => direct(a.item) - direct(b.item) || a.index - b.index)
      .map(({ item }) => item)
  }

  pageActions() {
    return PAGE_ACTIONS.flatMap((action) => {
      const button = document.querySelector(action.selector)
      if (!button) return []

      const name = button.getAttribute("aria-label") || button.getAttribute("title") || action.label
      return [ {
        id: action.id,
        kind: "action",
        label: name,
        hint: "Thread",
        keywords: [ action.label, ...action.keywords ],
        selector: action.selector,
        reveal: action.reveal
      } ]
    })
  }

  ticketItem(ticket, { jump = false } = {}) {
    const id = ticket.ticket_id ? `#${ticket.ticket_id}` : ""
    return {
      id: `conversation:${ticket.id}`,
      kind: jump ? "jump-ticket" : "conversation",
      label: ticket.subject,
      meta: [ id, ticket.contact, ticket.project ].filter(Boolean).join(" · "),
      hint: jump ? "Open ↵" : "Open",
      href: ticket.href
    }
  }

  contactItem(contact) {
    return {
      id: `contact:${contact.id}`,
      kind: "contact",
      label: contact.name,
      meta: contact.email && contact.email !== contact.name ? contact.email : "",
      hint: "Threads",
      href: contact.href
    }
  }

  articleItem(article) {
    return {
      id: `article:${article.id}`,
      kind: "article",
      label: article.title,
      meta: article.project,
      hint: "Article",
      href: article.href
    }
  }

  // Built here rather than sent by the server so it is on screen from the first keystroke, and so
  // it carries the query as typed rather than lowercased.
  seeAllItem() {
    const url = new URL(this.searchUrlValue, window.location.origin)
    url.searchParams.set("q", this.queryTarget.value.trim())
    return {
      id: "search:all",
      kind: "see-all",
      label: "See all results",
      meta: "",
      hint: "Search",
      href: `${url.pathname}${url.search}`
    }
  }

  recentItems(available) {
    const byId = new Map(available.map((item) => [ item.id, item ]))
    return this.recentIds.map((id) => byId.get(id)).filter(Boolean)
  }

  buildGroup(group) {
    const node = this.groupTemplateTarget.content.cloneNode(true)
    node.querySelector(".command-palette__group-label").textContent = group.label
    const items = node.querySelector(".command-palette__group-items")
    group.items.forEach((item) => items.appendChild(this.buildOption(item)))
    return node
  }

  buildOption(item) {
    const node = this.optionTemplateTarget.content.firstElementChild.cloneNode(true)
    node.querySelector(".command-palette__option-label").textContent = item.label
    node.querySelector(".command-palette__option-meta").textContent = item.meta || ""
    node.querySelector(".command-palette__option-hint").textContent = item.hint || ""
    node.dataset.commandId = item.id
    if (item.kind === "jump-ticket") node.classList.add("command-palette__option--jump")
    if (item.kind === "see-all") node.classList.add("command-palette__option--see-all")
    if (item.href) node.dataset.href = item.href
    if (item.selector) node.dataset.selector = item.selector
    if (item.reveal) node.dataset.reveal = "true"
    node.addEventListener("click", (event) => this.run(event))
    node.addEventListener("pointerover", () => this.highlight(node, { moved: true }))
    return node
  }

  highlight(option, { moved = false } = {}) {
    if (moved) this.highlightMoved = true
    this.options.forEach((item) => item.classList.remove("is-active"))
    if (!option) return

    option.classList.add("is-active")
    option.scrollIntoView({ block: "nearest" })
  }

  moveHighlight(delta) {
    const options = this.options
    if (options.length === 0) return

    const current = options.indexOf(this.highlighted)
    const start = current === -1 ? (delta > 0 ? -1 : 0) : current
    const next = (start + delta + options.length) % options.length
    this.highlight(options[next], { moved: true })
  }

  matches(item, query) {
    if (query === "") return true

    const haystack = [ item.label, item.meta, ...(item.keywords || []) ].filter(Boolean).join(" ").toLowerCase()
    if (haystack.includes(query)) return true

    let index = 0
    for (const character of query.replace(/\s/g, "")) {
      const found = haystack.indexOf(character, index)
      if (found === -1) return false
      index = found + 1
    }
    return true
  }

  // Commands only. A thread, contact or article is a search result, and recents list what the
  // empty palette can offer again without a query.
  remember(id) {
    if (!id || !/^(jump|action):/.test(id)) return

    const ids = this.recentIds.filter((value) => value !== id)
    ids.unshift(id)
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(0, RECENT_LIMIT)))
  }

  get recentIds() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]")
      return Array.isArray(stored) ? stored.filter((value) => typeof value === "string") : []
    } catch {
      return []
    }
  }

  get currentProjectId() {
    const match = window.location.pathname.match(/^\/projects\/(\d+)(?:\/|$)/)
    return match ? match[1] : ""
  }

  get typedQuery() {
    return this.queryTarget.value.trim().toLowerCase()
  }

  get options() {
    return Array.from(this.resultsTarget.querySelectorAll(".command-palette__option"))
  }

  get highlighted() {
    return this.resultsTarget.querySelector(".command-palette__option.is-active")
  }

  removeKeyListener() {
    document.removeEventListener("keydown", this.onKeydown)
  }
}
